import React, { useEffect, useMemo, useState } from "react";
import { track, ecommerce, carItem } from "/src/helpers/analytics";
import { useOutletContext, Link } from "react-router";
import { useApi } from "/src/helpers/Hooks/useApi";
import formatMoney from "/src/helpers/formatMoney";
import Button from "/src/helpers/components/button.jsx";
import BasketBar from "/src/helpers/components/basketBar.jsx";
import helpConfig from "/src/helpers/helpConfig";
import {
    ArrowLeftIcon, CheckIcon, InfoCircleIcon, PhoneIcon,
} from "/src/helpers/components/upsellIcons.jsx";

const gbp = (v) => `£${formatMoney(v)}`;

// The chosen main driver (and, when their DOB isn't on the booking, their stated age), kept in
// memory only for the lifetime of the loaded app (keyed by booking ref). Never persisted.
const rememberedDrivers = {};

const fmtDob = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : null);
const guestName = (g) => [g?.title, g?.firstName, g?.lastName].filter(Boolean).join(" ");
const fmtNice = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
const dateKey = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// "HH:mm" (local) from an ISO datetime — the server snaps times to the half-hour grid.
const timeKey = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const fmtNiceTime = (iso) => (iso ? `${fmtNice(iso)} · ${timeKey(iso)}` : "");
// Every half hour of the day, "00:00" … "23:30".
const HALF_HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
// Local ISO the API binds as a local DateTime (no zone suffix — the holiday is in resort time).
const joinDateTime = (date, time) => (date ? `${date}T${time || "10:00"}:00` : null);

// Half-hour picker that greys out anything outside the window on the edge days.
const TimeSelect = ({ value, onChange, minTime, maxTime }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary cursor-pointer w-[96px] shrink-0">
        {HALF_HOURS.map((t) => (
            <option key={t} value={t} disabled={(minTime && t < minTime) || (maxTime && t > maxTime)}>{t}</option>
        ))}
    </select>
);
const addDays = (iso, n) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const datesBetween = (startIso, endIso) => {
    const out = [];
    let d = startIso;
    while (d && endIso && d <= endIso) { out.push(d); d = addDays(d, 1); }
    return out;
};

// "Vehicle type: Mini Car|Doors: 3|..." -> the category pill + remaining spec chips,
// excluding the long inclusions blurb.
const carInfoParts = (carInformation) => {
    const parts = (carInformation || "")
        .split("|")
        .map((s) => s.trim())
        .filter((s) => s && !s.toLowerCase().startsWith("inclusions"));
    const typePart = parts.find((p) => p.toLowerCase().startsWith("vehicle type:"));
    return {
        category: typePart ? typePart.split(":")[1].trim() : null,
        chips: parts.filter((p) => p !== typePart).slice(0, 3),
    };
};

// The supplier's pipe-delimited AdditionalInformation ("FUEL - Full to Full |EXCESS - …|OTHER
// COSTS - Excess:650:EUR,Deposit:650:EUR|CANCELLATIONS - …|Terms and Conditions: …") -> the
// short rental facts shown in the detail modal (the T&Cs tail is shown at checkout). Only the
// labelled segments below are shown; anything else in the string is dropped.
const rentalFacts = (info) => {
    const facts = [];
    // No CANCELLATIONS row: the supplier's "No Fees Apply" isn't Olympic's cancellation position,
    // so cancellation terms aren't quoted on the car at all.
    const labels = { FUEL: "Fuel policy", EXCESS: "Excess", "OTHER COSTS": "Deposit & excess", "MANDATORY CHARGES": "Mandatory local charges", "NO SHOWS": "No-show" };
    (info || "").split("|").forEach((seg) => {
        const m = seg.trim().match(/^([A-Z ]+?)\s+-\s+(.+)$/);
        if (!m || !labels[m[1]]) return;
        let value = m[2].trim();
        // "Excess:650:EUR,Deposit:650:EUR" / "OutofHours:50.00:EUR" -> "Excess €650 · Deposit €650"
        if (/^[A-Za-z ]+:[0-9.]+:[A-Z]{3}/.test(value)) {
            value = value.split(",").map((part) => {
                const [name, amount, ccy] = part.split(":");
                const label = name.replace(/OutofHours/i, "Out of hours").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
                return `${label} ${ccy === "EUR" ? "€" : ccy === "GBP" ? "£" : `${ccy} `}${amount}`;
            }).join(" · ");
        }
        facts.push({ label: labels[m[1]], value });
    });
    return facts;
};

// Feather-style spec icons (prototype cars-page.jsx).
const iconSeat = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="7" r="3" /><path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
    </svg>
);
const iconBag = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 7h12v14H6z" /><path d="M9 7V4h6v3" />
    </svg>
);
const iconSmallBag = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="7" y="9" width="10" height="11" rx="1.5" /><path d="M10 9V7a2 2 0 0 1 4 0v2" />
    </svg>
);
const iconLargeBag = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="5" width="14" height="16" rx="1.5" /><path d="M9 5V3h6v2" /><line x1="12" y1="9" x2="12" y2="17" />
    </svg>
);

const SpecChip = ({ icon, label }) => (
    <span className="inline-flex items-center gap-[7px] text-[13px] text-content_2">
        <span className="text-emp_dark">{icon}</span>
        {label}
    </span>
);

const FilterGroup = ({ label, children }) => (
    <div className="mb-[22px] pb-[18px] border-b border-content_5 last:border-b-0 last:pb-0 last:mb-0">
        <div className="text-xs font-bold text-content_3 uppercase tracking-[0.06em] mb-3">{label}</div>
        {children}
    </div>
);

const AddCarHirePage = () => {
    const { model } = useOutletContext();
    const api = useApi();

    const [eligibility, setEligibility] = useState(null);
    const [depots, setDepots] = useState([]);
    const [guests, setGuests] = useState([]);
    const [search, setSearch] = useState(null);   // { pickupDate, dropoffDate, depotId, depotName, results }
    const [basket, setBasket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // search-bar values (live-applied, prototype CarSearchPanel)
    const [draftDepot, setDraftDepot] = useState(null);
    const [draftPickup, setDraftPickup] = useState("");
    const [draftDropoff, setDraftDropoff] = useState("");
    // "HH:mm" on the half-hour grid; seeded from the flight-derived window and re-synced from each
    // search response (the server clamps anything outside the holiday).
    const [draftPickupTime, setDraftPickupTime] = useState("");
    const [draftDropoffTime, setDraftDropoffTime] = useState("");
    // How many of the party travel in the car — iVector only returns vehicles with that many
    // seats, so this drives the search itself (a 17-person group would otherwise see no cars).
    const [draftPassengers, setDraftPassengers] = useState(null);
    // The main driver is chosen up front (popup, once); if that passenger has no usable date of
    // birth on the booking (anonymised/legacy data) a second step asks their age. Held in plain JS
    // variables only — survives in-app navigation, a full reload asks again.
    const [driverId, setDriverId] = useState(null);
    const [driverAge, setDriverAge] = useState(null);
    const [driverModalStep, setDriverModalStep] = useState(null); // null | "driver" | "age"
    const [modalDriverId, setModalDriverId] = useState("");
    const [modalAge, setModalAge] = useState("");

    // filters (supplier) + sort
    const [supplierFilter, setSupplierFilter] = useState(new Set());
    const [sort, setSort] = useState("price-asc");

    const [modalCar, setModalCar] = useState(null);

    useEffect(() => {
        (async () => {
            const elig = await api.carHireEligibility();
            setEligibility(elig);
            if (!elig?.canAdd) { setLoading(false); return; }
            const [depotList, basketRes, guestList] = await Promise.all([
                api.carHireDepots(), api.basketGet(), api.carHireGuests()
            ]);
            setDepots(depotList || []);
            setGuests(guestList || []);
            if (basketRes?.ok) setBasket(basketRes.data);

            // Seed the search bar from the booking so it reads sensibly before the first search.
            setDraftPickup(dateKey(elig.pickupDate));
            setDraftDropoff(dateKey(elig.dropoffDate));
            setDraftPickupTime(timeKey(elig.pickupDate));
            setDraftDropoffTime(timeKey(elig.dropoffDate));
            setDraftPassengers(Math.min(4, Math.max(1, (guestList || []).length || 1)));

            const searchAs = async (drv) => {
                setDriverId(drv.driverId);
                setDriverAge(drv.age ?? null);
                const res = await api.carHireSearch({ driverPassengerId: drv.driverId, driverAge: drv.age ?? null });
                if (res?.results) applySearch(res);
                else setError(res?.detail || "We couldn't search for cars on this booking.");
            };

            const remembered = rememberedDrivers[model?.bookingReference];
            const adults = (guestList || []).filter((g) => g.type === "Adult");
            if (remembered) {
                await searchAs(remembered);
            } else if (adults.length === 1 && adults[0].dateOfBirth) {
                // Only one possible driver and we know their DOB — nothing to ask.
                rememberedDrivers[model?.bookingReference] = { driverId: adults[0].bookingPassengerId };
                await searchAs({ driverId: adults[0].bookingPassengerId });
            } else if (adults.length === 1) {
                setModalDriverId(String(adults[0].bookingPassengerId));
                setDriverModalStep("age");
            } else {
                setDriverModalStep("driver");
            }
            setLoading(false);
        })();
    }, []);

    const applySearch = (res) => {
        setSearch(res);
        track("car_search", { results: res?.results?.length || 0, passengers: res?.passengers, driver_age: res?.driverAge, depot: res?.depotName });
        if (res?.results?.length) ecommerce.viewItemList("car_hire", "Car hire results", res.results.map((c) => carItem(c)));
        setDraftDepot(res?.depotId ?? null);
        setDraftPickup(dateKey(res?.pickupDate));
        setDraftDropoff(dateKey(res?.dropoffDate));
        setDraftPickupTime(timeKey(res?.pickupDate));
        setDraftDropoffTime(timeKey(res?.dropoffDate));
        setDraftPassengers(res?.passengers ?? null);
    };

    // The prototype applies search-bar changes live — no Update button. Dates stay clamped
    // in-window (options only cover the stay) and drop-off is kept after pick-up.
    const runSearch = async (over = {}) => {
        setError("");
        const res = await api.carHireSearch({
            depotId: over.depotId ?? draftDepot,
            pickupDate: joinDateTime(over.pickupDate ?? draftPickup, over.pickupTime ?? draftPickupTime),
            dropoffDate: joinDateTime(over.dropoffDate ?? draftDropoff, over.dropoffTime ?? draftDropoffTime),
            passengers: over.passengers ?? draftPassengers,
            driverPassengerId: over.driverPassengerId ?? driverId,
            driverAge: over.driverAge ?? driverAge
        });
        if (res?.results) applySearch(res);
        else setError(res?.detail || "We couldn't update the search — please check the dates.");
    };

    const onDepot = (depotId) => { setDraftDepot(depotId); runSearch({ depotId }); };
    const onPickup = (iso) => {
        let drop = draftDropoff;
        if (drop <= iso) drop = addDays(iso, 1);
        setDraftPickup(iso); setDraftDropoff(drop);
        runSearch({ pickupDate: iso, dropoffDate: drop });
    };
    const onDropoff = (iso) => { setDraftDropoff(iso); runSearch({ dropoffDate: iso }); };
    const onPickupTime = (t) => { setDraftPickupTime(t); runSearch({ pickupTime: t }); };
    const onDropoffTime = (t) => { setDraftDropoffTime(t); runSearch({ dropoffTime: t }); };
    const onPassengers = (n) => { setDraftPassengers(n); runSearch({ passengers: n }); };
    const modalDriver = guests.find((g) => String(g.bookingPassengerId) === String(modalDriverId));
    const confirmDriver = () => {
        if (!modalDriver) return;
        track("car_driver_selected", { has_dob: !!modalDriver.dateOfBirth });
        if (modalDriver.dateOfBirth) {
            // Real DOB on the booking — the server derives the age from it, no prompt needed.
            rememberedDrivers[model?.bookingReference] = { driverId: modalDriver.bookingPassengerId };
            setDriverId(modalDriver.bookingPassengerId);
            setDriverAge(null);
            setDriverModalStep(null);
            runSearch({ driverPassengerId: modalDriver.bookingPassengerId, driverAge: null });
        } else {
            setDriverModalStep("age");
        }
    };
    const confirmDriverAge = () => {
        const n = Number(modalAge);
        if (!n || n < 18 || n > 99 || !modalDriver) return;
        track("car_driver_age_entered", { age_band: n < 25 ? "18-24" : n < 70 ? "25-69" : "70+" });
        rememberedDrivers[model?.bookingReference] = { driverId: modalDriver.bookingPassengerId, age: n };
        setDriverId(modalDriver.bookingPassengerId);
        setDriverAge(n);
        setDriverModalStep(null);
        runSearch({ driverPassengerId: modalDriver.bookingPassengerId, driverAge: n });
    };

    const suppliers = useMemo(() => {
        const counts = new Map();
        (search?.results || []).forEach((c) => {
            if (c.supplier) counts.set(c.supplier, (counts.get(c.supplier) || 0) + 1);
        });
        return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [search]);

    const toggleSupplier = (s) => {
        const next = new Set(supplierFilter);
        next.has(s) ? next.delete(s) : next.add(s);
        setSupplierFilter(next);
    };
    const resetFilters = () => { setSupplierFilter(new Set()); };

    const filtered = useMemo(() => {
        const out = (search?.results || []).filter((c) =>
            supplierFilter.size === 0 || supplierFilter.has(c.supplier));
        out.sort((a, b) => sort === "price-desc" ? b.perDayPrice - a.perDayPrice : a.perDayPrice - b.perDayPrice);
        return out;
    }, [search, supplierFilter, sort]);
    const passengerOptions = Array.from({ length: Math.min(search?.maxPassengers || guests.length || 1, 9) }, (_, i) => i + 1);

    const rentalDays = useMemo(() => {
        const from = draftPickup || dateKey(search?.pickupDate);
        const to = draftDropoff || dateKey(search?.dropoffDate);
        if (!from || !to) return 0;
        return Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000));
    }, [draftPickup, draftDropoff, search]);

    // The basket holds at most one car; match on vehicle+supplier for the in-basket cue
    // (tokens change between searches).
    const isSelected = (car) => basket?.car &&
        basket.car.vehicleDescription === car.vehicleDescription && basket.car.supplier === car.supplier;
    const hasCarInBasket = !!basket?.car;

    const windowStart = dateKey(eligibility?.pickupDate);
    const windowEnd = dateKey(eligibility?.dropoffDate);
    const windowStartTime = timeKey(eligibility?.pickupDate);
    const windowEndTime = timeKey(eligibility?.dropoffDate);
    const windowDates = useMemo(() => datesBetween(windowStart, windowEnd), [windowStart, windowEnd]);

    const backLink = (
        <Link to="/" className="inline-flex items-center gap-1.5 text-emp_dark font-semibold text-sm self-start mb-4">
            <ArrowLeftIcon /> Back to My Booking
        </Link>
    );

    if (loading) {
        return (
            <div className="bg-white rounded-card shadow-card p-6">
                <h1 className="text-2xl font-poppins font-bold text-primary mb-2">Available Cars</h1>
                <p className="text-content_2">Finding cars for your trip…</p>
            </div>
        );
    }

    if (!eligibility?.canAdd) {
        return (
            <div>
                {backLink}
                <div className="bg-white rounded-card shadow-card p-6">
                    <h1 className="text-2xl font-poppins font-bold text-primary mb-2">Available Cars</h1>
                    <p className="text-content_2">{eligibility?.reason || "Car hire cannot be added to this booking."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            {backLink}

            {/* ── Gradient hero strip (prototype CarsHeroStrip) ────────────────────────────── */}
            <section className="rounded-card shadow-card text-white px-8 py-6 mb-[18px]
                                bg-[linear-gradient(95deg,#08355c,#0e7fc7)]">
                <div className="text-xs font-bold tracking-[0.1em] opacity-85 mb-1.5 uppercase">
                    Car hire{eligibility.destinationName ? ` · ${eligibility.destinationName}` : ""}
                </div>
                <h1 className="text-[30px] font-poppins font-bold leading-tight">Choose your car</h1>
                {search && (
                    <div className="text-sm opacity-90 mt-1">
                        {search.depotName} · {fmtNiceTime(search.pickupDate)} → {fmtNiceTime(search.dropoffDate)} · {rentalDays} {rentalDays === 1 ? "day" : "days"}
                    </div>
                )}
            </section>

            {error && <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* ── Search panel: live-applied, clamped to the holiday (prototype) ───────────── */}
            <section className="bg-white rounded-card shadow-card px-[22px] py-[18px] mb-[18px]">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:flex-wrap">
                    <label className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
                        <span className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em]">Pick-up &amp; drop-off location</span>
                        <select value={draftDepot ?? ""} onChange={(e) => onDepot(Number(e.target.value))}
                                className="w-full px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary cursor-pointer">
                            {depots.map((d) => (
                                <option key={d.depotId} value={d.depotId}>{d.name}{d.isDefault ? " (your airport)" : ""}</option>
                            ))}
                        </select>
                    </label>
                    <div className="flex flex-col gap-1.5 flex-1 min-w-[230px]">
                        <span className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em]">Pick-up</span>
                        <div className="flex gap-2">
                            <select value={draftPickup} onChange={(e) => onPickup(e.target.value)} aria-label="Pick-up date"
                                    className="flex-1 min-w-0 px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary cursor-pointer">
                                {windowDates.slice(0, -1).map((iso) => (
                                    <option key={iso} value={iso}>{fmtNice(iso)}</option>
                                ))}
                            </select>
                            {/* On arrival day nothing before the flight-derived earliest pick-up. */}
                            <TimeSelect value={draftPickupTime} onChange={onPickupTime}
                                        minTime={draftPickup === windowStart ? windowStartTime : null} />
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-1 min-w-[230px]">
                        <span className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em]">Drop-off</span>
                        <div className="flex gap-2">
                            <select value={draftDropoff} onChange={(e) => onDropoff(e.target.value)} aria-label="Drop-off date"
                                    className="flex-1 min-w-0 px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary cursor-pointer">
                                {windowDates.filter((iso) => iso > draftPickup).map((iso) => (
                                    <option key={iso} value={iso}>{fmtNice(iso)}</option>
                                ))}
                            </select>
                            {/* On departure day nothing after the latest drop-off (2h before the flight). */}
                            <TimeSelect value={draftDropoffTime} onChange={onDropoffTime}
                                        maxTime={draftDropoff === windowEnd ? windowEndTime : null} />
                        </div>
                    </div>
                    <label className="flex flex-col gap-1.5 min-w-[120px]">
                        <span className="text-[11px] font-bold text-content_3 uppercase tracking-[0.05em]">Passengers</span>
                        <select value={draftPassengers ?? ""} onChange={(e) => onPassengers(Number(e.target.value))}
                                className="w-full px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary cursor-pointer">
                            {passengerOptions.map((n) => (
                                <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>
                            ))}
                        </select>
                    </label>
                    <div className="shrink-0 px-4 py-2 bg-emp_soft rounded-[10px] text-center min-w-[78px] self-start sm:self-end">
                        <div className="text-[22px] font-extrabold text-emp_dark leading-none">{rentalDays}</div>
                        <div className="text-[11px] text-emp_dark font-semibold">{rentalDays === 1 ? "day" : "days"}</div>
                    </div>
                </div>
                <div className="mt-3 text-[12.5px] text-content_3 flex items-start gap-[7px]">
                    <span className="shrink-0 mt-0.5"><InfoCircleIcon size={14} /></span>
                    <span className="leading-relaxed">
                        Car hire is available within your stay only, from {fmtNiceTime(eligibility.pickupDate)} (an hour after you land)
                        to {fmtNiceTime(eligibility.dropoffDate)} (two hours before your flight home). Times are in resort local time.
                        Pick-up and drop-off are always the same place. We only show cars with enough seats for the passengers you choose.
                    </span>
                </div>
                <div className="mt-2 text-[12.5px] text-[#b66a0a] flex items-start gap-[7px]">
                    <span className="shrink-0 mt-0.5"><PhoneIcon size={14} /></span>
                    <span className="leading-relaxed">
                        Need to drop off at a different location? Please call our team on{" "}
                        <strong className="whitespace-nowrap">{helpConfig.phoneDisplay}</strong> to arrange it.
                    </span>
                </div>
            </section>

            {/* ── Selected car: Important Car Hire information (guide §4) ─────────────────── */}
            {basket?.car?.rentalConditions && (
                <details className="bg-white rounded-card shadow-card mb-[18px] open:pb-4">
                    <summary className="cursor-pointer px-5 py-4 font-poppins font-semibold text-primary">
                        Important Car Hire information — {basket.car.vehicleDescription}
                    </summary>
                    <div className="px-5 text-sm text-content_2 whitespace-pre-line max-h-80 overflow-y-auto">
                        {/* Suppliers delimit sections with ## markers — render them as line breaks. */}
                        {basket.car.rentalConditions.replace(/##\s*/g, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "")}
                    </div>
                </details>
            )}

            {/* ── Filters sidebar + results (prototype r-cars-layout) ─────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-[22px] items-start">
                <aside className="bg-white rounded-card shadow-card px-[22px] pt-[22px] pb-6 md:sticky md:top-4">
                    <div className="flex justify-between items-baseline mb-[18px]">
                        <h3 className="text-[17px] font-poppins font-bold text-primary">Filters</h3>
                        <button onClick={resetFilters} className="text-[13px] font-semibold text-emp_dark cursor-pointer hover:underline">Reset</button>
                    </div>

                    <FilterGroup label="Supplier">
                        {suppliers.length === 0 && <p className="text-[13px] text-content_3">No suppliers for this search.</p>}
                        {suppliers.map(([s, count]) => (
                            <label key={s} className="flex items-center gap-2.5 py-1.5 cursor-pointer select-none text-sm text-primary">
                                <input type="checkbox" checked={supplierFilter.has(s)} onChange={() => toggleSupplier(s)}
                                       className="w-4 h-4 accent-emp cursor-pointer" />
                                <span className="flex-1">{s}</span>
                                <span className="text-xs text-content_3">({count})</span>
                            </label>
                        ))}
                    </FilterGroup>
                </aside>

                <div className="flex flex-col gap-3.5 min-w-0">
                    <div className="flex justify-between items-center gap-3 flex-wrap px-1">
                        <div className="font-semibold text-primary">
                            {search ? (
                                <>
                                    <strong className="text-lg">{filtered.length}</strong>
                                    <span className="text-content_2 font-medium"> car{filtered.length !== 1 ? "s" : ""} available for your dates</span>
                                </>
                            ) : (
                                <span className="text-content_2 font-medium">Finding cars for your trip…</span>
                            )}
                        </div>
                        <label className="flex items-center gap-2 text-[13px] text-content_2">
                            Sort by
                            <select value={sort} onChange={(e) => setSort(e.target.value)}
                                    className="px-3.5 py-2 rounded-lg border border-content_5 bg-white text-[13px] text-primary cursor-pointer">
                                <option value="price-asc">Price · Low to high</option>
                                <option value="price-desc">Price · High to low</option>
                            </select>
                        </label>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="bg-white rounded-card shadow-card p-10 text-center text-content_2">
                            No cars match these filters.{" "}
                            <button onClick={resetFilters} className="text-emp_dark font-semibold cursor-pointer hover:underline">Reset filters</button>
                        </div>
                    ) : filtered.map((car) => {
                        const inBasket = isSelected(car);
                        const { category, chips } = carInfoParts(car.carInformation);
                        return (
                            <div key={car.token}
                                 className={`bg-white rounded-card overflow-hidden flex flex-col md:flex-row md:items-stretch
                                    ${inBasket ? "border-[1.5px] border-green shadow-[0_0_0_3px_rgba(31,158,110,0.12)]" : "shadow-card"}`}>
                                <div className="relative w-full aspect-video md:w-[280px] md:aspect-[4/3] md:shrink-0 bg-[#f0f4f8]">
                                    <img src={car.imageUrl} alt={car.vehicleDescription} loading="lazy"
                                         className="w-full h-full object-contain p-3 block" />
                                    {inBasket && (
                                        <span className="absolute top-3 right-3 inline-flex items-center gap-[5px] bg-green text-white px-2.5 py-1 rounded-full text-xs font-bold shadow">
                                            <CheckIcon size={13} /> In basket
                                        </span>
                                    )}
                                    {car.onRequest && (
                                        <span className="absolute top-3 left-3 bg-orange text-white px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap">
                                            On request
                                        </span>
                                    )}
                                </div>

                                <div className="px-5 py-5 md:px-6 flex flex-col flex-1 min-w-0">
                                    <h3 className="text-xl font-poppins font-bold text-primary">{car.vehicleDescription}</h3>
                                    <div className="text-xs text-content_2 mt-1.5">
                                        {category && (
                                            <span className="inline-block bg-emp_soft text-emp_dark px-[9px] py-0.5 rounded-full text-[11px] font-semibold mr-2">
                                                {category}
                                            </span>
                                        )}
                                        {car.supplier && <>Supplied by <strong className="text-primary">{car.supplier}</strong></>}
                                    </div>

                                    <div className="flex flex-wrap gap-x-[22px] gap-y-3 mt-4 items-center">
                                        <SpecChip icon={iconSeat} label={`${car.paxCapacity} pax`} />
                                        <SpecChip icon={iconBag} label={`${car.baggageCapacity} bags total`} />
                                        {car.smallBaggageCapacity > 0 && <SpecChip icon={iconSmallBag} label={`${car.smallBaggageCapacity} small`} />}
                                        {car.largeBaggageCapacity > 0 && <SpecChip icon={iconLargeBag} label={`${car.largeBaggageCapacity} large`} />}
                                    </div>

                                    {chips.length > 0 && (
                                        <div className="mt-3.5 text-[13px] text-content_2">{chips.join(" · ")}</div>
                                    )}
                                </div>

                                <div className="border-t md:border-t-0 md:border-l border-content_5 bg-bg_2 px-[22px] py-5
                                                flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-2 md:w-[220px] md:shrink-0">
                                    <div className="text-left md:text-right">
                                        <div className="text-[11px] text-content_3 uppercase tracking-[0.06em]">
                                            {rentalDays} {rentalDays === 1 ? "day" : "days"} · total
                                        </div>
                                        <div className="text-[30px] font-bold text-primary leading-none my-1">{gbp(car.price)}</div>
                                        <div className="text-xs text-content_3">{gbp(car.perDayPrice)}/day</div>
                                    </div>
                                    <button onClick={() => { if (inBasket) return; ecommerce.selectItem("car_hire", "Car hire results", carItem(car)); ecommerce.viewItem(carItem(car)); setModalCar(car); }} disabled={inBasket}
                                            className={`md:mt-1.5 md:w-full px-[18px] py-[11px] rounded-btn font-semibold text-sm inline-flex items-center justify-center gap-[7px]
                                                ${inBasket ? "bg-green text-white cursor-default"
                                                    : "bg-emp hover:bg-emp_dark text-white cursor-pointer"}`}>
                                        {inBasket ? <><CheckIcon size={15} /> In basket</>
                                            : hasCarInBasket ? "Switch to this car" : "Add to booking"}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {driverModalStep === "driver" && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-emp_deep/50 p-4"
                     role="dialog" aria-modal="true" aria-label="Who will be the main driver?">
                    <div className="bg-white rounded-card shadow-card w-full max-w-sm p-6">
                        <h2 className="text-xl font-poppins font-bold text-primary mb-1.5">Who's driving?</h2>
                        <p className="text-sm text-content_2 mb-4">
                            Car prices depend on the main driver's age, so tell us who'll be driving first.
                        </p>
                        {/* Adults as pickable rows rather than a dropdown, so the lead passenger —
                            first in the list, server-ordered — can be badged as such. */}
                        <div className="flex flex-col gap-2 mb-4">
                            {guests.filter((g) => g.type === "Adult").map((g, i) => {
                                const chosen = String(g.bookingPassengerId) === String(modalDriverId);
                                const dob = fmtDob(g.dateOfBirth);
                                return (
                                    <label key={g.bookingPassengerId}
                                           className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] border cursor-pointer
                                               ${chosen ? "border-emp bg-emp_soft" : "border-content_5 bg-white hover:border-emp"}`}>
                                        <input type="radio" name="main-driver" autoFocus={i === 0}
                                               value={g.bookingPassengerId} checked={chosen}
                                               onChange={(e) => setModalDriverId(e.target.value)}
                                               className="w-4 h-4 shrink-0 accent-emp cursor-pointer" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm text-primary font-medium truncate">{guestName(g)}</span>
                                            {dob && <span className="block text-[12px] text-content_3">{dob}</span>}
                                        </span>
                                        {g.isLead && (
                                            <span className="shrink-0 bg-[#e7f6ef] text-[#157a54] px-2 py-0.5 rounded-full text-[11px] font-bold">
                                                Lead passenger
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                        <Button variant="primary" onClick={confirmDriver}
                                className={modalDriver ? "" : "opacity-50 pointer-events-none"}>
                            Continue
                        </Button>
                    </div>
                </div>
            )}

            {driverModalStep === "age" && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-emp_deep/50 p-4"
                     role="dialog" aria-modal="true" aria-label="Main driver's age">
                    <div className="bg-white rounded-card shadow-card w-full max-w-sm p-6">
                        <h2 className="text-xl font-poppins font-bold text-primary mb-1.5">Main driver's age</h2>
                        <p className="text-sm text-content_2 mb-4">
                            We don't hold a date of birth for{" "}
                            <strong className="text-primary">{guestName(modalDriver)}</strong>
                            {" "}— please tell us how old they'll be at pick-up.
                        </p>
                        <input autoFocus type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
                               placeholder="e.g. 35" value={modalAge}
                               onChange={(e) => setModalAge(e.target.value.replace(/\D/g, "").slice(0, 2))}
                               onKeyDown={(e) => e.key === "Enter" && confirmDriverAge()}
                               className="w-full px-3 py-2.5 rounded-[10px] border border-content_5 bg-white text-sm text-primary mb-1" />
                        <p className="text-[12px] text-content_3 mb-4">The driver must be between 18 and 99.</p>
                        <Button variant="primary" onClick={confirmDriverAge}
                                className={Number(modalAge) >= 18 && Number(modalAge) <= 99 ? "" : "opacity-50 pointer-events-none"}>
                            Find cars
                        </Button>
                    </div>
                </div>
            )}

            {modalCar && (
                <CarDetailModal
                    car={modalCar}
                    guests={guests}
                    search={search}
                    hasCarInBasket={hasCarInBasket}
                    chosenDriverId={driverId}
                    onClose={() => setModalCar(null)}
                    onAdded={(vm) => { setBasket(vm); setModalCar(null); }}
                />
            )}

            <BasketBar basket={basket} />
        </div>
    );
};

// Detail modal: specs, optional extras ("Reserve now, pay at the rental desk." unless prepaid),
// provider-conditional excess insurance, and the add/replace action (guide §4).
const CarDetailModal = ({ car, guests, search, hasCarInBasket, chosenDriverId, onClose, onAdded }) => {
    const api = useApi();
    const [extras, setExtras] = useState(null);
    const [qty, setQty] = useState({});
    const [insurance, setInsurance] = useState(undefined);   // undefined = not asked, null = none offered
    const [insuranceAccepted, setInsuranceAccepted] = useState(false);
    const [error, setError] = useState("");

    // Insurance is quoted in the lead passenger's name (the server flags and lists them first).
    const leadAdult = guests.find((g) => g.isLead) || guests.find((g) => g.type === "Adult") || guests[0];

    useEffect(() => {
        (async () => {
            const list = await api.carHireExtras(car.token);
            setExtras((list || []).filter((e) => !e.includedInPrice && !e.mandatory));
            // Excess insurance is provider-conditional: only offered when the provider quotes.
            try {
                const quote = await api.carHireInsurance({
                    token: car.token,
                    leadGuestFirstName: leadAdult?.firstName,
                    leadGuestLastName: leadAdult?.lastName,
                });
                setInsurance(quote?.premiumName ? quote : null);
            } catch { setInsurance(null); }
        })();
    }, [car.token]);

    const add = async () => {
        setError("");
        const res = await api.basketSetCar({
            searchToken: car.token,
            // Display-only echoes for the basket/checkout lines; prices stay server-derived.
            vehicleDescription: car.vehicleDescription,
            supplier: car.supplier,
            imageUrl: car.imageUrl,
            pickupDate: search?.pickupDate,
            dropoffDate: search?.dropoffDate,
            depotName: search?.depotName,
            extras: Object.entries(qty).filter(([, q]) => q > 0).map(([extraToken, quantity]) => ({ extraToken, quantity })),
            insuranceAccepted: insuranceAccepted && !!insurance,
            insuranceToken: insuranceAccepted ? insurance?.token : null,
            guestIds: [],
            driverId: chosenDriverId || 0
        });
        if (res.ok) {
            ecommerce.addToCart(carItem(car), res.data?.car?.estimatedTotal ?? car.price);
            track("car_added", { supplier: car.supplier, extras: Object.values(qty).filter((q) => q > 0).length, insurance: insuranceAccepted && !!insurance });
            onAdded(res.data);
        }
        else setError(res.data?.detail || "We couldn't add that car — please try again.");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-emp_deep/50 p-0 md:p-6"
             onClick={onClose}>
            <div className="bg-white rounded-t-card md:rounded-card shadow-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                        <h2 className="text-xl font-poppins font-bold text-primary">{car.vehicleDescription}</h2>
                        {car.supplier && <p className="text-sm text-content_2">Supplied by <strong className="text-primary">{car.supplier}</strong></p>}
                    </div>
                    <button onClick={onClose} aria-label="Close" className="text-content_3 hover:text-primary text-2xl leading-none cursor-pointer">×</button>
                </div>

                <img src={car.imageUrl} alt="" className="w-full h-40 object-contain mb-4" />

                {/* specs (guide §4: pax, total/small/large baggage) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[["Passengers", car.paxCapacity],
                      ["Total baggage", car.baggageCapacity],
                      ["Small bags", car.smallBaggageCapacity],
                      ["Large bags", car.largeBaggageCapacity],
                    ].map(([label, value]) => (
                        <div key={label} className="bg-emp_soft rounded-xl px-3 py-2 text-center">
                            <div className="text-lg font-semibold text-primary">{value}</div>
                            <div className="text-xs text-content_2">{label}</div>
                        </div>
                    ))}
                </div>
                {car.doors > 0 && <p className="text-sm text-content_2 mb-4">{car.doors} doors</p>}

                {/* supplier rental facts (from the supplier's AdditionalInformation via iVector) */}
                {(() => {
                    const facts = rentalFacts(car.additionalInformation);
                    if (facts.length === 0) return null;
                    return (
                        <div className="mb-4 bg-bg_2 rounded-xl px-4 py-3">
                            <h3 className="font-poppins font-bold text-primary mb-2 text-sm">Rental facts from {car.supplier || "the supplier"}</h3>
                            <dl className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-[13px]">
                                {facts.map((f) => (
                                    <React.Fragment key={f.label}>
                                        <dt className="text-content_3 font-semibold">{f.label}</dt>
                                        <dd className="text-primary">{f.value}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                        </div>
                    );
                })()}

                {/* optional extras */}
                {extras === null ? (
                    <p className="text-sm text-content_2 mb-4">Loading extras…</p>
                ) : extras.length > 0 && (
                    <div className="mb-4">
                        <h3 className="font-poppins font-bold text-primary mb-1">Optional extras</h3>
                        <p className="text-xs text-content_3 mb-2">Reserve now, pay at the rental desk — unless marked prepaid.</p>
                        <div className="space-y-2">
                            {extras.map((ex) => (
                                <div key={ex.token} className="flex items-center gap-3 text-sm">
                                    <input type="number" min="0" max="9" value={qty[ex.token] || 0}
                                           onChange={(e) => setQty({ ...qty, [ex.token]: Number(e.target.value) })}
                                           className="w-14 h-9 rounded-lg border border-content_4 px-2 text-center" />
                                    <span className="flex-1 text-primary">{ex.description}</span>
                                    <span className="text-content_2 shrink-0">
                                        {gbp(ex.price)}{ex.prePaid ? " (prepaid)" : " · pay at desk"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* provider-conditional excess insurance */}
                {insurance && (
                    <div className="mb-4 bg-emp_soft rounded-xl p-4">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={insuranceAccepted}
                                   onChange={(e) => setInsuranceAccepted(e.target.checked)} className="mt-1 cursor-pointer" />
                            <span className="text-sm">
                                <span className="font-semibold text-primary">{insurance.premiumName}</span>
                                <span className="text-content_2"> — {gbp(insurance.premiumAmount)}</span>
                                {insurance.coveredItems?.length > 0 && (
                                    <ul className="list-disc ml-5 mt-1 text-content_2">
                                        {insurance.coveredItems.slice(0, 6).map((c) => <li key={c}>{c}</li>)}
                                    </ul>
                                )}
                            </span>
                        </label>
                    </div>
                )}

                {error && <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mb-3">{error}</div>}

                <div className="flex flex-col sm:flex-row gap-2 items-center justify-between">
                    <div>
                        <div className="text-xl font-poppins font-bold text-primary">{gbp(car.price)}<span className="text-sm font-normal text-content_2"> total</span></div>
                        <div className="text-xs text-content_3">{gbp(car.perDayPrice)}/day{insuranceAccepted && insurance ? " + insurance" : ""}</div>
                    </div>
                    <Button variant="primary" onClick={add}>
                        {hasCarInBasket ? "Switch to this car" : "Add to booking"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddCarHirePage;
